import { useContext } from 'react';
import { AuthContext } from '../authContext/context';
import { UserRole } from '../constants/auth.constants';
import { Card, Row, Col, Alert } from 'react-bootstrap';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import GroupIcon from '@mui/icons-material/Group';
import BusinessIcon from '@mui/icons-material/Business';
import GavelIcon from '@mui/icons-material/Gavel';
import DashboardIcon from '@mui/icons-material/Dashboard';
import GroupCodesBreakdown from '../screens/dashboard/page';

/**
 * RoleDashboard Component
 * Shows different dashboard content based on user role
 */
export default function RoleDashboard() {
  const authCtx = useContext(AuthContext);
  const { role, user, isSuperAdmin } = authCtx;

  // Superadmin Dashboard
  const SuperAdminDashboard = () => (
    <>
      <Alert variant="info">
        <strong>Welcome, Super Admin!</strong> You have full system access.
      </Alert>
      <Row className="g-3">
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <GroupIcon style={{ fontSize: 40, color: '#0d6efd' }} />
              <h3>Users</h3>
              <p className="text-muted">Manage all users</p>
              <a href="/users" className="btn btn-primary btn-sm">View Users</a>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <BusinessIcon style={{ fontSize: 40, color: '#198754' }} />
              <h3>Partners</h3>
              <p className="text-muted">Manage partners</p>
              <a href="/partners" className="btn btn-success btn-sm">View Partners</a>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <GavelIcon style={{ fontSize: 40, color: '#ffc107' }} />
              <h3>Solicitors</h3>
              <p className="text-muted">Manage solicitors</p>
              <a href="/solicitors" className="btn btn-warning btn-sm">View Solicitors</a>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <PersonAddIcon style={{ fontSize: 40, color: '#dc3545' }} />
              <h3>Create User</h3>
              <p className="text-muted">Add new user</p>
              <a href="/users/create" className="btn btn-danger btn-sm">Create</a>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Group Codes Breakdown Section */}
      <div className="mt-4">
        <GroupCodesBreakdown />
      </div>
    </>
  );

  // Partner Dashboard
  const PartnerDashboard = () => (
    <>
      <Alert variant="primary">
        <strong>Welcome, Partner!</strong> Manage your organization.
      </Alert>
      <Row className="g-3">
        <Col md={4}>
          <Card className="text-center">
            <Card.Body>
              <GroupIcon style={{ fontSize: 40, color: '#0d6efd' }} />
              <h3>My Users</h3>
              <p className="text-muted">Manage your users</p>
              <a href={`/users?parent_id=${user?.userId}`} className="btn btn-primary btn-sm">
                View My Users
              </a>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="text-center">
            <Card.Body>
              <PersonAddIcon style={{ fontSize: 40, color: '#198754' }} />
              <h3>Add User</h3>
              <p className="text-muted">Create partner user</p>
              <a href={`/users/create?parent_id=${user?.userId}`} className="btn btn-success btn-sm">
                Create User
              </a>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="text-center">
            <Card.Body>
              <DashboardIcon style={{ fontSize: 40, color: '#6c757d' }} />
              <h3>Customers</h3>
              <p className="text-muted">View customers</p>
              <a href="/customers" className="btn btn-secondary btn-sm">View Customers</a>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );

  // Partner User Dashboard
  const PartnerUserDashboard = () => (
    <>
      <Alert variant="info">
        <strong>Welcome!</strong> Access your customer management tools.
      </Alert>
      <Row className="g-3">
        <Col md={4}>
          <Card className="text-center">
            <Card.Body>
              <GroupIcon style={{ fontSize: 40, color: '#0d6efd' }} />
              <h3>Customers</h3>
              <p className="text-muted">Manage customers</p>
              <a href="/customers" className="btn btn-primary btn-sm">View Customers</a>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="text-center">
            <Card.Body>
              <DashboardIcon style={{ fontSize: 40, color: '#198754' }} />
              <h3>Leads</h3>
              <p className="text-muted">View leads</p>
              <a href="/leads" className="btn btn-success btn-sm">View Leads</a>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="text-center">
            <Card.Body>
              <PersonAddIcon style={{ fontSize: 40, color: '#6c757d' }} />
              <h3>Profile</h3>
              <p className="text-muted">Update profile</p>
              <a href="/account/manage" className="btn btn-secondary btn-sm">View Profile</a>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );

  // Solicitor Dashboard
  const SolicitorDashboard = () => (
    <>
      <Alert variant="warning">
        <strong>Welcome, Solicitor!</strong> Manage your cases.
      </Alert>
      <Row className="g-3">
        <Col md={4}>
          <Card className="text-center">
            <Card.Body>
              <GavelIcon style={{ fontSize: 40, color: '#ffc107' }} />
              <h3>Solicitors</h3>
              <p className="text-muted">View Solicitors</p>
              <a href="/solicitors" className="btn btn-warning btn-sm">View Solicitors</a>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="text-center">
            <Card.Body>
              <GroupIcon style={{ fontSize: 40, color: '#0d6efd' }} />
              <h3>Customers</h3>
              <p className="text-muted">View customers</p>
              <a href="/customers" className="btn btn-primary btn-sm">View Customers</a>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="text-center">
            <Card.Body>
              <PersonAddIcon style={{ fontSize: 40, color: '#6c757d' }} />
              <h3>Profile</h3>
              <p className="text-muted">Update profile</p>
              <a href="/account/manage" className="btn btn-secondary btn-sm">View Profile</a>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );

  // Regular User Dashboard
  const UserDashboard = () => (
    <>
      <Alert variant="secondary">
        <strong>Welcome!</strong> Access your dashboard.
      </Alert>
      <Row className="g-3">
        <Col md={6}>
          <Card className="text-center">
            <Card.Body>
              <DashboardIcon style={{ fontSize: 40, color: '#0d6efd' }} />
              <h3>Dashboard</h3>
              <p className="text-muted">View overview</p>
              <a href="/dashboard" className="btn btn-primary btn-sm">View Dashboard</a>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="text-center">
            <Card.Body>
              <PersonAddIcon style={{ fontSize: 40, color: '#6c757d' }} />
              <h3>Profile</h3>
              <p className="text-muted">Update profile</p>
              <a href="/account/manage" className="btn btn-secondary btn-sm">View Profile</a>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );

  // Render based on role
  const renderDashboard = () => {
    switch (role) {
      case UserRole.SUPERADMIN:
        return <SuperAdminDashboard />;
      case UserRole.PARTNER:
        return <PartnerDashboard />;
      case UserRole.PARTNER_USER:
        return <PartnerUserDashboard />;
      case UserRole.SOLICITOR:
        return <SolicitorDashboard />;
      case UserRole.USER:
      default:
        return <UserDashboard />;
    }
  };

  return (
    <div className="role-dashboard">
      <div className="page-header pb-3">
        <h1 className="mt-0 mb-0">Dashboard</h1>
        <p className="text-muted">
          Logged in as: <strong>{user?.username}</strong> ({user?.email})
        </p>
      </div>
      {renderDashboard()}
    </div>
  );
}
